import { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X } from "lucide-react";

const Header = ({ openModal }: { openModal: () => void }) => {
    const { t, i18n } = useTranslation();
    const [language, setLanguage] = useState(i18n.language || "en");
    const [isOpen, setIsOpen] = useState(false);
    const [scrollPosition, setScrollPosition] = useState(0);
    const [isVisible, setIsVisible] = useState(true);

    const toggleMenu = () => setIsOpen(!isOpen);

    const handleNavLinkClick = () => {
        setIsOpen(false);
    };

    // Handle escape key for mobile menu
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === "Escape" && isOpen) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("keydown", handleEscape);
            // Prevent body scroll when menu is open
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }

        return () => {
            document.removeEventListener("keydown", handleEscape);
            document.body.style.overflow = "unset";
        };
    }, [isOpen]);

    useEffect(() => {
        const handleScroll = () => {
            const currentScrollPos = window.pageYOffset;
            setIsVisible(
                scrollPosition > currentScrollPos || currentScrollPos < 10
            );
            setScrollPosition(currentScrollPos);
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, [scrollPosition]);

    useEffect(() => {
        const handleLanguageChange = (lng: string) => {
            setLanguage(lng);
            document.documentElement.dir = lng === "ar" ? "rtl" : "ltr";
        };

        i18n.on("languageChanged", handleLanguageChange);
        return () => {
            i18n.off("languageChanged", handleLanguageChange);
        };
    }, [i18n]);

    const handleLanguageChange = (lang: string) => {
        i18n.changeLanguage(lang);
    };

    return (
        <>
            <header
                role="banner"
                className={`w-full sticky top-0 z-40 backdrop-blur-md bg-neutral-white/90 border-b border-neutral-light transition-all duration-300 ${
                    isVisible
                        ? "transform translate-y-0"
                        : "transform -translate-y-full"
                }`}>
                <div className="container-custom">
                    <div className="flex justify-between items-center py-4 md:py-6">
                        <div className="animate-fade-in-left">
                            <NavLink 
                                to="/" 
                                className="block"
                                aria-label={t("header.home")}>
                                <img
                                    src="/assets/LogoDark.png"
                                    alt="NawasTech - نواس تك"
                                    className="w-[150px] md:w-[180px] transition-transform hover:scale-105"
                                    width="180"
                                    height="60"
                                    loading="eager"
                                    decoding="async"
                                />
                            </NavLink>
                        </div>

                        {/* Desktop Navigation */}
                        <div className="hidden lg:flex items-center space-x-8 rtl:space-x-reverse">
                            <nav 
                                role="navigation" 
                                aria-label={t("header.mainNavigation")}
                                className="flex items-center space-x-6 rtl:space-x-reverse">
                                <NavLink
                                    to=""
                                    className={({ isActive }) =>
                                        `text-lg font-semibold transition-all duration-300 hover:text-primary relative focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-sm px-2 py-1 ${
                                            isActive
                                                ? "text-primary after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-primary"
                                                : "text-text hover:text-primary"
                                        }`
                                    }
                                    onMouseUp={e => (e.currentTarget as HTMLElement).blur()}
                                    onBlur={e => (e.currentTarget as HTMLElement).blur()}
                                    >
                                    {t("header.home")}
                                </NavLink>
                                <NavLink
                                    to="/pricing"
                                    className={({ isActive }) =>
                                        `text-lg font-semibold transition-all duration-300 hover:text-primary relative focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-sm px-2 py-1 ${
                                            isActive
                                                ? "text-primary after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-primary"
                                                : "text-text hover:text-primary"
                                        }`
                                    }
                                    onMouseUp={e => (e.currentTarget as HTMLElement).blur()}
                                    onBlur={e => (e.currentTarget as HTMLElement).blur()}
                                    >
                                    {t("header.pricing")}
                                </NavLink>
                                <NavLink
                                    to="features"
                                    className={({ isActive }) =>
                                        `text-lg font-semibold transition-all duration-300 hover:text-primary relative focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-sm px-2 py-1 ${
                                            isActive
                                                ? "text-primary after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-primary"
                                                : "text-text hover:text-primary"
                                        }`
                                    }
                                    aria-label={t("header.features") + ' - ' + t("featuresPage.description")}
                                    onMouseUp={e => (e.currentTarget as HTMLElement).blur()}
                                    onBlur={e => (e.currentTarget as HTMLElement).blur()}
                                >
                                    {t("header.features")}
                                </NavLink>
                                <NavLink
                                    to="contact-us"
                                    className={({ isActive }) =>
                                        `text-lg font-semibold transition-all duration-300 hover:text-primary relative focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-sm px-2 py-1 ${
                                            isActive
                                                ? "text-primary after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-primary"
                                                : "text-text hover:text-primary"
                                        }`
                                    }
                                    aria-label={t("header.contactUs") + ' - ' + t("contactUs.heading")}
                                    onMouseUp={e => (e.currentTarget as HTMLElement).blur()}
                                    onBlur={e => (e.currentTarget as HTMLElement).blur()}
                                >
                                    {t("header.contactUs")}
                                </NavLink>
                            </nav>

                            <div className="flex items-center space-x-4 rtl:space-x-reverse">
                                <button
                                    onClick={openModal}
                                    className="btn btn-primary">
                                    {t("header.requestFreeCopy")}
                                </button>

                                <button
                                    onClick={() =>
                                        handleLanguageChange(
                                            language === "ar" ? "en" : "ar"
                                        )
                                    }
                                    className="btn btn-outline text-sm px-4 py-2"
                                    onMouseUp={e => (e.currentTarget as HTMLElement).blur()}
                                    onBlur={e => (e.currentTarget as HTMLElement).blur()}
                                    >
                                    {language === "ar" ? "EN" : "عربي"}
                                </button>
                            </div>
                        </div>

                        {/* Mobile menu button */}
                        <div className="lg:hidden flex items-center space-x-4 rtl:space-x-reverse">
                            <button
                                onClick={() =>
                                    handleLanguageChange(
                                        language === "ar" ? "en" : "ar"
                                    )
                                }
                                className="text-sm px-3 py-1 border border-primary text-primary rounded-md hover:bg-primary hover:text-white transition-colors">
                                {language === "ar" ? "EN" : "عربي"}
                            </button>

                            <button
                                onClick={toggleMenu}
                                className="p-2 rounded-lg hover:bg-neutral-light transition-colors">
                                {isOpen ? (
                                    <X className="w-6 h-6 text-primary" />
                                ) : (
                                    <Menu className="w-6 h-6 text-primary" />
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Mobile Navigation */}
            <nav
                id="mobile-menu"
                role="navigation"
                aria-label={t("header.mobileNavigation")}
                className={`fixed top-0 left-0 w-full h-screen bg-primary z-30 transform transition-transform duration-300 lg:hidden ${
                    isOpen ? "translate-y-0" : "-translate-y-full"
                }`}
                aria-hidden={!isOpen}>
                <div className="flex flex-col justify-center items-center h-full space-y-8">
                    <NavLink
                        to=""
                        onClick={handleNavLinkClick}
                        className="text-2xl font-bold text-white hover:text-accent transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-primary rounded-sm px-4 py-2">
                        {t("header.home")}
                    </NavLink>
                    <NavLink
                        to="/pricing"
                        onClick={handleNavLinkClick}
                        className="text-2xl font-bold text-white hover:text-accent transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-primary rounded-sm px-4 py-2">
                        {t("header.pricing")}
                    </NavLink>
                    <NavLink
                        to="features"
                        onClick={handleNavLinkClick}
                        className="text-2xl font-bold text-white hover:text-accent transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-primary rounded-sm px-4 py-2">
                        {t("header.features")}
                    </NavLink>
                    <NavLink
                        to="contact-us"
                        onClick={handleNavLinkClick}
                        className="text-2xl font-bold text-white hover:text-accent transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-primary rounded-sm px-4 py-2"
                        aria-label={t("header.contactUs") + ' - ' + t("contactUs.heading")}
                        >
                        {t("header.contactUs")}
                    </NavLink>
                    <button
                        onClick={() => {
                            openModal();
                            handleNavLinkClick();
                        }}
                        className="btn btn-accent text-xl px-8 py-4 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-primary"
                        aria-label={t("header.requestFreeCopy")}>
                        {t("header.requestFreeCopy")}
                    </button>
                </div>
            </nav>
        </>
    );
};

export default Header;
